import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { downloadDatabookQnaExcel, getDatabookInsights, type DatabookInsights } from "@/lib/api/databook";
import { BackendError } from "@/lib/backend";
import { DatabookVariancePanel } from "./DatabookVariancePanel";
import { DatabookQnaPanel } from "./DatabookQnaPanel";
import {
  nextInsightPosition,
  previousInsightPosition,
  insightPosition,
  type InsightCursor,
} from "./databookInsightNavigation";

interface Props {
  insights?: DatabookInsights;
  kind: "variances" | "qna";
  projectId?: string;
  versionId?: string;
  previewRevision: number;
  sheetName: string | null;
  sheetNames: string[];
  sheetListError: boolean;
  onSelectSheet: (sheetName: string) => void;
  onRetryWorkbook: () => void;
  onClose: () => void;
}

export function DatabookInsightsInspector({
  insights,
  kind, projectId, versionId, previewRevision, sheetName, sheetNames, sheetListError,
  onSelectSheet, onRetryWorkbook, onClose,
}: Props) {
  const query = useQuery({
    queryKey: ["databook-insights", projectId, versionId, previewRevision],
    queryFn: ({ signal }) => getDatabookInsights(projectId!, versionId!, signal),
    enabled: !insights && Boolean(projectId && versionId),
    staleTime: Infinity,
    retry: false,
  });
  const data = insights ?? query.data;
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const downloadQna = async () => {
    if (!projectId || !versionId || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadDatabookQnaExcel(projectId, versionId);
    } catch {
      setDownloadError("Q&A could not be downloaded. Please try again.");
    } finally {
      setDownloading(false);
    }
  };
  const [cursor, setCursor] = useState<InsightCursor | null>(null);
  if (cursor && cursor.sheetName !== sheetName) setCursor(null);
  const entrySheets = kind === "qna"
    ? (data?.questions ?? []).map(question => question.databook_tab)
    : (data?.variances ?? []).map(finding => finding.sheet_name);
  const position = insightPosition(entrySheets, sheetName, cursor);
  const previousPosition = previousInsightPosition(entrySheets, sheetNames, sheetName, position);
  const navigate = (index: number) => {
    const targetSheet = entrySheets[index];
    setCursor({ sheetName: targetSheet ?? sheetName, position: index });
    if (targetSheet && targetSheet !== sheetName) onSelectSheet(targetSheet);
  };
  const staleVersion = query.error instanceof BackendError && query.error.status === 409;
  const panelProps = {
    sheetName: sheetName ?? "Databook",
    position,
    total: entrySheets.length,
    loading: !insights && !sheetListError && query.isPending,
    error: sheetListError || (!insights && query.isError),
    onRetry: () => {
      if (sheetListError || staleVersion || !versionId) onRetryWorkbook();
      else void query.refetch();
    },
    onNext: () => navigate(position === entrySheets.length
      ? 0 : nextInsightPosition(entrySheets, sheetNames, sheetName, position)),
    onPrevious: () => navigate(previousPosition),
    previousDisabled: previousPosition < 0,
    onClose,
  };
  return kind === "qna"
    ? <DatabookQnaPanel {...panelProps} onDownload={() => void downloadQna()} downloading={downloading} downloadError={downloadError} question={data?.questions[position] ?? null} />
    : <DatabookVariancePanel {...panelProps} finding={data?.variances[position] ?? null} />;
}
